// AI chat thread helpers — server-backed via `sdk.aiChat.threads.*` and
// `sdk.aiChat.messages.list`. AiChatService persists thread + message
// records in Mongo (chat_threads / chat_messages); these helpers thread
// the SDK calls into the workspace's reactive root state so the
// AppAssistant panel + HomePrompt strip read from the same place.
//
// State surface populated here:
//   - root._aiThreads     — array of { id, title, updated_at, ... }
//   - root._aiThreadId    — currently active thread id
//   - root._aiThreadsLoading — true while a list fetch is in flight
//
// AppAssistantThreadPicker (workspace/components/AppAssistantThreadPicker.js)
// reads these keys and calls back into these functions via `el.call(...)`.
//
// Error handling: catch + console.warn rather than fail loudly. The
// picker stays usable even when threads RPC is unavailable — it just
// shows "No saved threads yet" and the inline thread continues to work
// against `root.assistantMessages`.

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
import { scopeGen, scopeStale } from './loadPrivateData.js'

const _logErr = (label, err) => {
  console.warn(`[aiAssistantThreads] ${label}:`, err?.message || err)
}

// Unwrap an SDK list response into a plain array — the aiChat list methods
// may return a bare array or an envelope ({ data } / { items }).
const _shellRows = (res) => (Array.isArray(res) ? res : res?.data || res?.items || [])

const _normalizeThread = (t) => ({
  id: t?.id || t?._id,
  // KEY-IN-STATE — AppAssistantThreadPicker resolves this through polyglot,
  // and a real (tenant) thread title passes through untranslated.
  title: t?.title || t?.subject || 'workspace.assistant.threadFallback',
  updatedAt: t?.updated_at || t?.updatedAt || t?.created_at || null,
  // Tenant key — AiChatThread.workspace (server@22ff64d4, 2026-07-14
  // WORKSPACE-scoping decision). The server is the real scoping gate
  // (AiChatController.listThreads → resolveAiChatScope); this is carried
  // through only so AppAssistantThreadPicker can do a belt-and-suspenders
  // filter against a stale-JWT race. `required:false` during the server's
  // backfill window, so pre-backfill rows normalize to `null` here —
  // callers must treat `null` as "unscoped, don't hide" rather than a
  // mismatch.
  workspaceId: t?.workspace || t?.workspaceId || t?.workspace_id || null
})

// Shared auth guard for every ai-chat write. Returns `true` when the
// SDK has a usable token; false otherwise. Wrapped in a try/catch so
// `getService('auth')` throwing inside the flat-method proxy (during
// SDK boot, before services register) doesn't escape as an unhandled
// rejection — the original guard hit that exact path.
const _hasAuthToken = (sdk) => {
  if (!sdk) return false
  try {
    if (typeof sdk.getAuthToken === 'function') {
      const tok = sdk.getAuthToken()
      if (tok) return true
    }
  } catch {}
  try {
    if (typeof sdk.isAuthenticated === 'function') {
      return !!sdk.isAuthenticated()
    }
  } catch {}
  return false
}

// List the user's recent threads into root._aiThreads. Idempotent —
// if a load is already in flight, bails and lets the in-flight one
// settle. Bails when caller isn't signed in (pre-bootShell hydration).
//
// `root._aiThreadsLoadAttempted` is set ONLY after a successful (or
// error-but-completed) fetch — it survives the auth bail so a caller
// (e.g. the picker's onInit) can still re-call after sign-in.
export const aiChatListThreads = async function aiChatListThreads(root) {
  if (!root) return
  if (root._aiThreadsLoading) return
  const sdk = shellGetSDK()
  if (!_hasAuthToken(sdk)) return
  // Join the workspace-switch epoch: threads are WORKSPACE-scoped, and a list
  // request issued in tenant A that resolves after a switch to tenant B would
  // repaint A's personalized threads over B's wiped state ("loading old AI
  // threads" — per Nika 2026-08-01). Capture the epoch before the request;
  // drop the response when stale (the new tenant's own refetch owns the data).
  const _gen = scopeGen()
  root.update({ _aiThreadsLoading: true })
  try {
    const aiChat = await sdk.getService('aiChat')
    const res = await aiChat.threads.list({ limit: 30 })
    if (scopeStale(_gen)) return
    const raw = _shellRows(res)
    const threads = raw.map(_normalizeThread).filter((t) => !!t.id)
    root.update({
      _aiThreads: threads,
      _aiThreadsLoading: false,
      _aiThreadsLoadAttempted: true
    })
  } catch (err) {
    if (scopeStale(_gen)) return
    _logErr('list failed', err)
    root.update({ _aiThreadsLoading: false, _aiThreadsLoadAttempted: true })
  }
}

// Load a thread's history into root.assistantMessages. The AppAssistant
// panel already renders from that key, so the swap shows up immediately
// after this resolves. `_aiThreadId` reflects the active thread.
export const aiChatLoadThread = async function aiChatLoadThread(root, threadId) {
  if (!root || !threadId) return
  const sdk = shellGetSDK()
  if (!_hasAuthToken(sdk)) return
  root.update({
    _aiThreadId: threadId,
    assistantMessages: [],
    assistantTyping: false
  })
  try {
    const aiChat = await sdk.getService('aiChat')
    const res = await aiChat.messages.list(threadId, { limit: 200 })
    const raw = _shellRows(res)
    // Service may return rows in either order — sort by created_at asc
    // so the panel renders oldest → newest top-to-bottom.
    const sorted = raw.slice().sort((a, b) => {
      const at = new Date(a?.created_at || a?.createdAt || 0).getTime()
      const bt = new Date(b?.created_at || b?.createdAt || 0).getTime()
      return at - bt
    })
    const messages = sorted.map((m) => ({
      role: m?.role || 'assistant',
      content: m?.content || m?.text || '',
      _streaming: false
    }))
    root.update({ assistantMessages: messages })
  } catch (err) {
    _logErr('load failed', err)
  }
}

// Create a new empty thread + select it. Pushes the new thread to the
// top of _aiThreads optimistically so the picker reflects the change
// without a refetch.
export const aiChatCreateThread = async function aiChatCreateThread(root) {
  if (!root) return
  const sdk = shellGetSDK()
  if (!_hasAuthToken(sdk)) return
  try {
    const aiChat = await sdk.getService('aiChat')
    const res = await aiChat.threads.create({
      title: this.call('polyglot', 'workspace.simone.newThread')
    })
    const created = _normalizeThread(res?.data || res)
    if (!created.id) {
      _logErr('create returned no id', res)
      return
    }
    root.update({
      _aiThreads: [created, ...(root._aiThreads || [])],
      _aiThreadId: created.id,
      assistantMessages: [],
      assistantTyping: false
    })
  } catch (err) {
    _logErr('create failed', err)
  }
}
